import { forwardRef } from "react";
import { type VariantProps, cva, cx } from "cva";
import type { ComponentPropsWithRef, ElementType, FunctionComponent, ReactNode, JSX } from "react";
import type { ButtonColor, ButtonSize, ButtonVariant } from "./Button.types.ts";
import type { NonFunctionProperties } from "../utils.ts";
import Typography from "../Typography";

const buttonRecipe = cva({
  base: "kn-btn",
  variants: {
    color: {
      primary: "kn-btn-primary",
      secondary: "kn-btn-secondary",
      tertiary: "kn-btn-tertiary",
    } satisfies Record<ButtonColor, string>,
    size: {
      small: "kn-btn-small",
      large: "kn-btn-large",
    } satisfies Record<ButtonSize, string>,
    variant: {
      contained: "kn-btn-contained",
      outlined: "kn-btn-outlined",
      text: "kn-btn-text",
    } satisfies Record<ButtonVariant, string>,
  },
  defaultVariants: {
    color: "primary",
    size: "large",
    variant: "contained",
  },
});

const buttonIconContainerRecipe = cva({
  base: "kn-btn-icon-container",
  variants: {
    position: {
      left: "kn-btn-icon-container-left",
      right: "kn-btn-icon-container-right",
    },
    size: {
      large: "kn-btn-icon-container-large",
      small: "kn-btn-icon-container-small",
    } satisfies Record<ButtonSize, string>,
  },
  defaultVariants: {
    size: "large",
  },
});

const buttonIconStyles = {
  large: "kn-btn-icon-large",
  small: "kn-btn-icon-small",
} satisfies Record<ButtonSize, string>;

type ButtonRecipeVariants = VariantProps<typeof buttonRecipe>;

// Define the type for the base button props
type BaseButtonProps = ButtonRecipeVariants & {
  left?: ReactNode;
  leftClassName?: string;
  right?: ReactNode;
  rightClassName?: string;
  innerClassName?: string;

  /**
   * @deprecated Use `innerClassName` instead
   */
  textClassName?: string;
};

// Define the type for the button props
export type ButtonProps<T extends ElementType = "button"> = BaseButtonProps &
  Omit<ComponentPropsWithRef<T>, keyof BaseButtonProps | "as"> & {
    as?: T;
  };

// Define the type for the button icon props
export type ButtonIconProps<T extends ElementType = "button"> = Omit<
  ComponentPropsWithRef<T>,
  keyof ButtonRecipeVariants | "as"
> &
  ButtonRecipeVariants & {
    as?: T;
    innerClassName?: string;
  };

// Define the button component with forwardRef
type ButtonComponent<T extends ElementType = "button"> = NonFunctionProperties<
  FunctionComponent<ButtonProps<T>>
> & {
  <T extends ElementType = "button">(props: ButtonProps<T>): JSX.Element;
  Icon: ButtonIconComponent;
};

type ButtonIconComponent<T extends ElementType = "button"> = NonFunctionProperties<
  FunctionComponent<ButtonIconProps<T>>
> & {
  <T extends ElementType = "button">(props: ButtonIconProps<T>): JSX.Element;
};

// Create the Button component
const Button = forwardRef((props, ref) => {
  const {
    as = "button" as ElementType,
    children,
    className,
    color = "primary",
    size = "large",
    variant = "contained",
    left,
    leftClassName,
    right,
    rightClassName,
    innerClassName,
    textClassName,
    type = "button",
    ...rest
  } = props;
  return (
    <Typography
      as={as}
      variant="button"
      {...rest}
      ref={ref}
      className={cx(buttonRecipe({ color, size, variant }), className)}
      type={as === "button" ? type : undefined}
    >
      {/* Render the left icon container */}
      {left ? (
        <div className={cx(buttonIconContainerRecipe({ position: "left", size }), leftClassName)}>
          {left}
        </div>
      ) : null}

      {/* Render the text */}
      <div className={innerClassName || textClassName}>{children}</div>

      {/* Render the right icon container */}
      {right ? (
        <div className={cx(buttonIconContainerRecipe({ position: "right", size }), rightClassName)}>
          {right}
        </div>
      ) : null}
    </Typography>
  );
}) satisfies Omit<ButtonComponent, "Icon"> as unknown as ButtonComponent;

Button.displayName = "Button";

// Create the Button.Icon component
Button.Icon = forwardRef((props, ref) => {
  const { children, className, size = "large", type = "button", ...rest } = props;
  return (
    <Button
      {...rest}
      ref={ref}
      className={cx(size && buttonIconStyles[size], className)}
      size={size}
      type={type}
    >
      {children}
    </Button>
  );
}) satisfies Omit<ButtonIconComponent, "Icon"> as unknown as ButtonIconComponent;

Button.Icon.displayName = "Button.Icon";

export default Button;
